// contexts/ChameleonProvider.tsx
import React, { type ReactNode, useCallback, useState } from 'react';
import { ChameleonContext, type ChameleonEntityType, type ChameleonState } from './ChameleonContext';
import { clearChameleonSession, INACTIVE_CHAMELEON, readChameleonSession, writeChameleonSession } from '@/api';
import { clearViewSelection } from '@/utils';

export function ChameleonProvider({ children }: { children: ReactNode }) {
    // sessionStorage is the source of truth for the outbound headers; this state is
    // only the React-side mirror used for rendering (badges, the demo toggle label).
    // No setter: every identity change goes through a full page reload, so this is
    // read once at mount and cannot drift from storage during the page's lifetime.
    const [chameleon] = useState<ChameleonState>(() => readChameleonSession() ?? INACTIVE_CHAMELEON);

    const activate = useCallback((entityType: ChameleonEntityType, entityId: number, entityName: string) => {
        writeChameleonSession({ active: true, entityId, entityType, entityName });
        // Drop the customer/date selection made as the previous identity — the new
        // entity may not be able to see it, which is the classic post-switch 403.
        clearViewSelection();
        // Do NOT call setChameleon() before reload — the re-render would fire
        // useEffect hooks that dispatch API requests in a half-set context,
        // causing a burst of 500s. Same rationale as deactivate() below.
        window.location.reload();
    }, []);

    const deactivate = useCallback(() => {
        clearChameleonSession();
        clearViewSelection();
        // Reload immediately — do NOT update React state first, because the
        // re-render can trigger useEffect sync hooks (e.g. useCustomer) that
        // write stale values back to storage before the reload completes.
        window.location.reload();
    }, []);

    return (
        <ChameleonContext.Provider value={{ chameleon, activate, deactivate }}>{children}</ChameleonContext.Provider>
    );
}
